import type { NavSubLink } from "../types/navigation";
import { cn } from "@workspace/ui/lib/utils";

/**
 * 전역 네비게이션 뱃지/도트 프리미티브 SSOT (JCG-3023 후속).
 *
 * 소비처:
 *  - `Menu.tsx` — 데스크탑 상단 NAV(트리거 도트 + hero 카드 pill) + 모바일 드롭다운 행
 *  - `article-shell/IconRail.tsx` — 접힌 레일 아이콘 도트 + 드로어 섹션 행/플라이아웃 하위 행 pill
 *
 * 이 파일이 생기기 전에는 `navBadgeClassNames` 가 두 컴포넌트에 복제돼 있어 색 하나 바꿀 때마다
 * 양쪽을 동시에 고쳐야 했다(menu-grid.md §4-5 의 "동시에 바꾼다" 주의사항). 새 뱃지/도트가
 * 필요하면 반드시 여기에 추가하고 두 surface 가 같은 시각 언어를 쓰도록 한다.
 *
 * 시각(시간)에 따라 켜지고 꺼지는 뱃지 주입은 `@/lib/nav-timed-badges` 담당 — 여기엔 표현만 둔다.
 */

export type NavBadge = NonNullable<NavSubLink["badge"]>;

/**
 * 뱃지 hue 3단 위계 (menu-grid.md §4-5):
 *   Now = red(지금 활성) · NEW = amber(새 것) · UPDATE = orange(갱신) · 곧 마감 = red(긴급)
 */
export const navBadgeClassNames: Record<NavBadge, string> = {
  Now: "bg-red-600 text-white dark:bg-red-400 dark:text-red-950",
  NEW: "bg-amber-500 text-white dark:bg-amber-400 dark:text-amber-950",
  // 브랜드(감자 amber/orange) 톤 — NEW(amber)와 구분되도록 orange.
  UPDATE: "bg-orange-500 text-white dark:bg-orange-400 dark:text-orange-950",
  // 마감 임박(긴급) 톤 — 현재 활성 신호 Now와 같은 red 시맨틱.
  // dark-mode-colors.md §2 "비-amber solid 배지는 light 만 한 단계 진하게": red 는 -500 이 아니라
  // light `bg-red-600`(흰 글씨 ≈4.8:1, AA 통과) + dark `bg-red-400`/near-black 글씨.
  "곧 마감": "bg-red-600 text-white dark:bg-red-400 dark:text-red-950",
  준비중: "bg-slate-500 text-white dark:bg-slate-400 dark:text-slate-950",
};

/** 뱃지 pill — 라벨 옆에 텍스트로 의미를 전달하는 유일한 신호(도트는 decorative). */
export function NavBadgePill({
  badge,
  tone,
}: {
  badge: NavBadge;
  tone?: NavSubLink["badgeTone"];
}) {
  return (
    <span
      className={cn(
        "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold",
        tone === "red" ? navBadgeClassNames.Now : navBadgeClassNames[badge],
      )}
    >
      {badge}
    </span>
  );
}

/**
 * 네비게이션 도트 (menu-grid.md §4-5). 모두 `aria-hidden` decorative 이므로
 * **의미는 반드시 같은 섹션의 pill 이 전달해야 한다** — pill 없는 도트 금지.
 *
 *  - `NavPingDot` = 현재 푸시 섹션. `tone="red"`는 `Now`, 기본 amber는 업데이트 신호.
 *  - `NavStaticDot` = 파동 없이 코어만 필요한 surface의 경량 신호.
 *
 * 크기/위치는 surface 마다 다르므로(상단 트리거 h-1.5 · 레일 아이콘 h-2) className 으로 받는다.
 * `NavPingDot` 은 내부 halo 가 `absolute` 라 className 에 위치 지정(`relative`/`absolute`)이 필요하다.
 */
/**
 * 도트 accent 색 — ping 코어와 정적 도트가 **같은 색이어야** 위계(halo 유무)만으로 세기가 갈린다.
 * 두 컴포넌트가 각자 하드코딩하면 한쪽만 바뀌어 색이 갈리므로 여기서 한 번만 정의한다.
 */
const NAV_DOT_ACCENT_CLS = "rounded-full bg-amber-500 dark:bg-amber-400";

export function NavPingDot({
  className,
  tone = "amber",
}: {
  className?: string;
  tone?: "amber" | "red";
}) {
  return (
    <span aria-hidden="true" className={cn("flex", className)}>
      <span
        className={cn(
          "absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 motion-reduce:hidden",
          tone === "red" ? "bg-red-400" : "bg-amber-400",
        )}
      />
      <span
        className={cn(
          "relative inline-flex h-full w-full rounded-full",
          tone === "red" ? "bg-red-600 dark:bg-red-400" : NAV_DOT_ACCENT_CLS,
        )}
      />
    </span>
  );
}

/** halo 없는 정적 도트 — reduced-motion 사용자가 보는 amber ping 코어와 동일하다. */
export function NavStaticDot({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn("shrink-0", NAV_DOT_ACCENT_CLS, className)}
    />
  );
}
